import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import KeyedImage from "@/components/KeyedImage";
import { TARGETS, SKULL_TARGET, TROPHY_TARGET, ASSETS } from "@/data/catalog";

const DIFFICULTY_COLORS = {
  easy:   { label: "Easy",   color: "#00FFFF" },
  medium: { label: "Medium", color: "#FFD700" },
  hard:   { label: "Hard",   color: "#FF007F" },
  skull:  { label: "Cursed", color: "#FF3B30" },
  trophy: { label: "Trophy", color: "#FFD700" },
};

const MOTION_LABEL = {
  linear: "Linear",
  bounce: "Bouncy",
  spin:   "Spinning",
  zigzag: "Zig-zag",
  rocket: "Rocket",
};

// Specials appear in their own row.
const SPECIALS = [
  {
    ...SKULL_TARGET,
    description: "A cursed skull. Hitting it costs you coins. Stay away unless it's a boss-drop you're intercepting.",
  },
  {
    ...TROPHY_TARGET,
    description: "The Golden Trophy — only appears in Trophy Trial. Hit it before your 3 arrows run out.",
  },
];

export default function TargetsScreen() {
  const nav = useNavigate();
  return (
    <div className="relative w-full h-full arcade-bg overflow-auto" data-testid="targets-screen">
      <div className="sticky top-0 z-20 backdrop-blur-md bg-[#0B0C10]/60 border-b border-white/5 safe-top">
        <div className="max-w-5xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            className="hud-pill hud-pill--cyan"
            onClick={() => nav("/")}
            data-testid="targets-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#00FFFF]" />
            <span className="text-[#00FFFF] font-display text-sm">Home</span>
          </button>
          <h1 className="font-display font-black text-2xl sm:text-3xl neon-pink">TARGETS</h1>
          <div className="w-24" />
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 sm:p-8">
        <p className="text-center text-white/70 font-body mb-6 sm:mb-10">
          Every silly critter you'll meet in the arena.
        </p>

        <h2 className="font-display text-xl neon-cyan mb-4 tracking-wider">REGULAR</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-10">
          {TARGETS.map((t) => <TargetCard key={t.id} target={t} />)}
        </div>

        <h2 className="font-display text-xl neon-yellow mb-4 tracking-wider">SPECIAL</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
          {SPECIALS.map((t) => <TargetCard key={t.id} target={t} special />)}
        </div>
      </div>
    </div>
  );
}

function TargetCard({ target, special }) {
  const diff = DIFFICULTY_COLORS[target.difficulty] || DIFFICULTY_COLORS.easy;
  const src = ASSETS[target.id];
  return (
    <div
      className="neon-card p-4 sm:p-5 flex gap-4 items-center"
      style={{ borderColor: `${diff.color}55` }}
      data-testid={`target-card-${target.id}`}
    >
      <div
        className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden"
        style={{ boxShadow: `0 0 16px ${diff.color}33` }}
      >
        {src ? (
          <KeyedImage src={src} alt={target.name} className="w-full h-full object-contain p-2" />
        ) : target.emoji ? (
          <div className="text-4xl" style={{ filter: `drop-shadow(0 0 12px ${diff.color})` }}>
            {target.emoji}
          </div>
        ) : null}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-display font-black text-base sm:text-lg truncate" style={{ color: diff.color }}>
            {target.name}
          </h3>
          <span
            className="text-[10px] font-display uppercase tracking-wider px-2 py-0.5 rounded-full border"
            style={{ color: diff.color, borderColor: `${diff.color}66`, background: `${diff.color}14` }}
          >
            {diff.label}
          </span>
          {MOTION_LABEL[target.motion] && (
            <span className="text-[10px] font-display uppercase tracking-wider px-2 py-0.5 rounded-full border border-white/20 text-white/70">
              {MOTION_LABEL[target.motion]}
            </span>
          )}
        </div>
        <p className="font-body text-sm text-white/70 mt-1">{target.description}</p>
        <div className="mt-2 flex items-center gap-4 text-xs font-display uppercase tracking-wider text-white/60">
          <span>
            Reward:{" "}
            <span style={{ color: target.coin < 0 ? "#FF3B30" : "#FFD700" }}>
              {target.coin > 0 ? `+${target.coin}` : target.coin === 0 ? "—" : target.coin} coins
            </span>
          </span>
          {!special && (
            <span>Unlocks at LVL {target.minLevel}</span>
          )}
        </div>
      </div>
    </div>
  );
}
